import React, { useEffect, useState } from 'react';
import { Save, AlertCircle, CheckCircle, Smartphone, RefreshCw, ExternalLink, ToggleLeft, ToggleRight, Info } from 'lucide-react';
import api from '../api';

export default function AppVersion() {
  const [settings, setSettings] = useState({
    version_name: '1.0.0',
    version_code: 2,
    download_url: 'https://layanan.synexa.co.id/downloads/layanansynexa.apk',
    release_notes: '',
    force_update: false,
    min_version: 1,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [originalSettings, setOriginalSettings] = useState(null);

  const loadSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/admin/app-version');
      setSettings(res.data);
      setOriginalSettings(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal memuat pengaturan versi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSettings(); }, []);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setSuccess('');
  };

  const handleToggleForceUpdate = () => {
    setSettings(prev => ({ ...prev, force_update: !prev.force_update }));
    setSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    // Validate
    if (!settings.version_name.trim()) {
      setError('Nama versi tidak boleh kosong');
      setSaving(false);
      return;
    }
    if (settings.version_code < 1) {
      setError('Kode versi harus >= 1');
      setSaving(false);
      return;
    }
    if (!settings.download_url.trim()) {
      setError('URL download tidak boleh kosong');
      setSaving(false);
      return;
    }

    try {
      const res = await api.put('/api/admin/app-version', {
        version_name: settings.version_name.trim(),
        version_code: parseInt(settings.version_code, 10),
        download_url: settings.download_url.trim(),
        release_notes: settings.release_notes.trim(),
        force_update: settings.force_update,
        min_version: parseInt(settings.min_version, 10) || 1,
      });
      setSettings(res.data);
      setOriginalSettings(res.data);
      setSuccess('Pengaturan versi APK berhasil disimpan!');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalSettings) {
      setSettings(originalSettings);
      setSuccess('');
      setError('');
    }
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h2>📱 Manajemen Versi APK</h2>
        </div>
        <div className="page-loading">Memuat pengaturan versi...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>📱 Manajemen Versi APK</h2>
            <p className="page-desc">Kelola versi aplikasi mobile Android (auto-update check)</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {hasChanges && (
              <button className="btn btn-secondary" onClick={handleReset}>
                Reset
              </button>
            )}
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || !hasChanges}
            >
              {saving ? (
                <><RefreshCw size={16} className="spinner" /> Menyimpan...</>
              ) : (
                <><Save size={16} /> Simpan</>
              )}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: 20 }}>
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 16px', background: '#d1fae5',
          border: '1px solid #a7f3d0', borderRadius: 'var(--radius)',
          color: '#065f46', fontWeight: 500, fontSize: '0.85rem',
          marginBottom: 20
        }}>
          <CheckCircle size={16} />
          <span>{success}</span>
        </div>
      )}

      {/* Info Banner */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '16px 20px', background: 'var(--info-tint-bg, #eff6ff)',
        border: '1px solid var(--info-tint-border, #bfdbfe)',
        borderRadius: 'var(--radius-lg)', marginBottom: 20,
        fontSize: '0.85rem', color: 'var(--info-text, #1e40af)', lineHeight: 1.6
      }}>
        <Info size={18} style={{ marginTop: 2, flexShrink: 0 }} />
        <div>
          <strong>Cara kerja:</strong> Aplikasi mobile akan otomatis memeriksa versi saat dibuka.
          Jika <code>version_code</code> di server lebih tinggi dari versi terinstall,
          pengguna akan melihat dialog update. Jika <strong>force_update</strong> aktif,
          pengguna wajib update sebelum bisa menggunakan aplikasi.
        </div>
      </div>

      {/* Version Info Card */}
      <div style={{
        background: 'white', borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow)', border: '1px solid var(--gray-200)',
        overflow: 'hidden', marginBottom: 20
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid var(--gray-100)',
          display: 'flex', alignItems: 'center', gap: 12
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'var(--primary-tint-bg, #eff6ff)', color: 'var(--primary, #3b82f6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Smartphone size={20} />
          </div>
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--gray-600)' }}>
              Informasi Versi
            </h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: 2 }}>
              Versi saat ini yang akan dideteksi oleh aplikasi mobile
            </p>
          </div>
        </div>

        <div style={{ padding: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
            {/* Version Name */}
            <div className="form-group">
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 6, display: 'block' }}>
                Nama Versi
              </label>
              <input
                type="text"
                value={settings.version_name}
                onChange={e => handleChange('version_name', e.target.value)}
                placeholder="1.0.0"
                style={{
                  padding: '10px 14px', border: '2px solid var(--gray-200)',
                  borderRadius: 'var(--radius)', fontSize: '1rem', fontWeight: 600,
                  color: 'var(--gray-700)', outline: 'none', width: '100%',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--primary, #3b82f6)'}
                onBlur={e => e.target.style.borderColor = 'var(--gray-200)'}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 4 }}>
                Contoh: 1.2.0, 2.0.0-beta
              </p>
            </div>

            {/* Version Code */}
            <div className="form-group">
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 6, display: 'block' }}>
                Kode Versi (integer)
              </label>
              <input
                type="number"
                min={1}
                value={settings.version_code}
                onChange={e => handleChange('version_code', parseInt(e.target.value, 10) || 1)}
                style={{
                  padding: '10px 14px', border: '2px solid var(--gray-200)',
                  borderRadius: 'var(--radius)', fontSize: '1rem', fontWeight: 600,
                  color: 'var(--gray-700)', outline: 'none', width: '100%',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--primary, #3b82f6)'}
                onBlur={e => e.target.style.borderColor = 'var(--gray-200)'}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 4 }}>
                Naikkan saat rilis versi baru (harus lebih besar dari versi sebelumnya)
              </p>
            </div>

            {/* Min Version */}
            <div className="form-group">
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 6, display: 'block' }}>
                Versi Minimum
              </label>
              <input
                type="number"
                min={1}
                value={settings.min_version}
                onChange={e => handleChange('min_version', parseInt(e.target.value, 10) || 1)}
                style={{
                  padding: '10px 14px', border: '2px solid var(--gray-200)',
                  borderRadius: 'var(--radius)', fontSize: '1rem', fontWeight: 600,
                  color: 'var(--gray-700)', outline: 'none', width: '100%',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--primary, #3b82f6)'}
                onBlur={e => e.target.style.borderColor = 'var(--gray-200)'}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 4 }}>
                Versi minimum yang didukung
              </p>
            </div>
          </div>

          {/* Download URL */}
          <div className="form-group" style={{ marginTop: 20 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 6, display: 'block' }}>
              URL Download APK
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="url"
                value={settings.download_url}
                onChange={e => handleChange('download_url', e.target.value)}
                placeholder="https://layanan.synexa.co.id/downloads/layanansynexa.apk"
                style={{
                  padding: '10px 14px', border: '2px solid var(--gray-200)',
                  borderRadius: 'var(--radius)', fontSize: '0.95rem',
                  color: 'var(--gray-700)', outline: 'none', flex: 1,
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--primary, #3b82f6)'}
                onBlur={e => e.target.style.borderColor = 'var(--gray-200)'}
              />
              {settings.download_url && (
                <a
                  href={settings.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '10px 12px', background: 'var(--gray-50)',
                    border: '2px solid var(--gray-200)', borderRadius: 'var(--radius)',
                    color: 'var(--gray-500)', textDecoration: 'none', fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                  }}
                  title="Buka link download"
                >
                  <ExternalLink size={14} /> Test
                </a>
              )}
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 4 }}>
              URL lengkap ke file APK yang akan diunduh pengguna
            </p>
          </div>

          {/* Release Notes */}
          <div className="form-group" style={{ marginTop: 20 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 6, display: 'block' }}>
              Catatan Rilis (Release Notes)
            </label>
            <textarea
              rows={4}
              value={settings.release_notes}
              onChange={e => handleChange('release_notes', e.target.value)}
              placeholder={"- Fitur baru: Profil pengguna\n- Perbaikan bug\n- Peningkatan performa"}
              style={{
                padding: '10px 14px', border: '2px solid var(--gray-200)',
                borderRadius: 'var(--radius)', fontSize: '0.95rem',
                color: 'var(--gray-700)', outline: 'none', width: '100%',
                transition: 'border-color 0.2s', resize: 'vertical',
                fontFamily: 'inherit',
              }}
              onFocus={e => e.target.style.borderColor = 'var(--primary, #3b82f6)'}
              onBlur={e => e.target.style.borderColor = 'var(--gray-200)'}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 4 }}>
              Ditampilkan kepada pengguna saat dialog update muncul
            </p>
          </div>

          {/* Force Update Toggle */}
          <div style={{
            marginTop: 20, padding: '16px 20px',
            background: settings.force_update ? '#fef3c7' : 'var(--gray-50)',
            border: `2px solid ${settings.force_update ? '#fbbf24' : 'var(--gray-200)'}`,
            borderRadius: 'var(--radius-lg)',
            transition: 'all 0.2s',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer',
            }} onClick={handleToggleForceUpdate}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: settings.force_update ? '#fbbf24' : 'var(--gray-200)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}>
                  {settings.force_update ? (
                    <ToggleRight size={20} color="#92400e" />
                  ) : (
                    <ToggleLeft size={20} color="var(--gray-500)" />
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--gray-700)' }}>
                    Force Update (Wajib Update)
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: 2 }}>
                    {settings.force_update
                      ? '⚠️ Pengguna WAJIB update sebelum bisa menggunakan aplikasi'
                      : 'Pengguna bisa menutup dialog dan tetap menggunakan versi lama'}
                  </div>
                </div>
              </div>
              <div style={{
                width: 48, height: 26, borderRadius: 13,
                background: settings.force_update ? '#f59e0b' : 'var(--gray-300)',
                position: 'relative', transition: 'all 0.3s',
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: 11,
                  background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  position: 'absolute', top: 2,
                  left: settings.force_update ? 24 : 2,
                  transition: 'left 0.3s',
                }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Card */}
      <div style={{
        background: 'white', borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow)', border: '1px solid var(--gray-200)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid var(--gray-100)',
          display: 'flex', alignItems: 'center', gap: 12
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--gray-600)' }}>
            👁️ Preview Response API
          </h3>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <pre style={{
            background: '#1e293b', color: '#e2e8f0', padding: '16px 20px',
            borderRadius: 'var(--radius)', fontSize: '0.85rem',
            fontFamily: 'JetBrains Mono, Fira Code, monospace',
            overflow: 'auto', margin: 0, lineHeight: 1.6,
          }}>
            {JSON.stringify({
              version_name: settings.version_name,
              version_code: parseInt(settings.version_code, 10),
              download_url: settings.download_url,
              release_notes: settings.release_notes || '(kosong)',
              force_update: settings.force_update,
              min_version: parseInt(settings.min_version, 10) || 1,
            }, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
}
